import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PackageService } from '@app/issue-certificate/package.service';
import { User } from '@app/_models';
import { Certificate } from '@app/_models/certificate';
import { Package } from '@app/_models/package';
import { ReportSearch } from '@app/_models/reportSearch';
import { AccountService, AlertService } from '@app/_services';
import { ConfigDataService } from '@app/_services/config-data.service';
import { SearchService } from '@app/_services/search.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { FileService } from '@app/_services/file.service';
import { PrintCertificateState } from '@app/_models/printCertificateState';

@Component({
  selector: 'app-print-certificate',
  templateUrl: './print-certificate.component.html',
  styleUrls: ['./print-certificate.component.less'],
})
export class PrintCertificateComponent implements OnInit, OnDestroy {

  @ViewChild('closeRequestCancelModalBtn') closeRequestCancelModalBtn: ElementRef;

  certificateReportForm: UntypedFormGroup;
  cancelPackageRequestForm: UntypedFormGroup;
  submitted = false;
  issueDateFrom: NgbDateStruct;
  issueDateTo: NgbDateStruct;
  loading = false;
  gridReady = true;
  printCertificateState: PrintCertificateState;
  reportSearch = new ReportSearch();
  issueDateFromSearch: string;
  issueDateToSearch: string;
  user: User;
  modelLoading = false;
  startDate: string;
  packages: Package[];
  selectedTypes = new Set<string>();
  selectedStatuses = new Set<string>();
  selectedEnteredBy = new Set<string>();
  currentDate = new Date();
  canceledPackageSelect: Package;
  emptyCertificate = new Certificate();
  selectedFile: any;
  uploadSuccess = false;
  isDeleting: boolean;
  requestCertificate: boolean;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private searchService: SearchService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private fileService: FileService,
    private packageService: PackageService,
    private configDataService: ConfigDataService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.requestCertificate = this.router.url.includes('requestCertificate');
    this.issueDateFrom = { year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate() };
    this.issueDateTo = this.issueDateFrom;
    this.user = this.accountService.userValue;
    this.cancelPackageRequestForm = this.formBuilder.group({
      cancelReason: [''],
      packageAmountReversed: [false]
    });
    this.certificateReportForm = this.formBuilder.group({
      packageNumber: [''],
      policyNumber: [''],
      chasisNumber: [''],
      firstName: [''],
      lastName: [''],
      userName: [''],
      contactNumber: [''],
      issueDateFrom: [this.issueDateFrom],
      issueDateTo: [this.issueDateFrom],
      dealerId: [''],
    });
    
    this.printCertificateState = this.packageService.getPrintCertificateState();
    if (this.printCertificateState != null) {
      this.reportSearch = this.printCertificateState.reportSearch;
      this.packages = this.printCertificateState.packageList;
      this.currentDate = new Date(this.reportSearch.issueDateFrom);
      this.issueDateFrom = { year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate() };
      this.currentDate = new Date(this.reportSearch.issueDateTo);
      this.issueDateTo = { year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate() };
      this.certificateReportForm.controls['packageNumber'].setValue(this.reportSearch.packageNumber);
      this.certificateReportForm.controls['chasisNumber'].setValue(this.reportSearch.chasisNumber);
      this.certificateReportForm.controls['firstName'].setValue(this.reportSearch.firstName);
      this.certificateReportForm.controls['lastName'].setValue(this.reportSearch.lastName);
      this.certificateReportForm.controls['userName'].setValue(this.reportSearch.userName);
      this.certificateReportForm.controls['contactNumber'].setValue(this.reportSearch.contactNumber);
      this.certificateReportForm.controls['issueDateFrom'].setValue(this.issueDateFrom);
      this.certificateReportForm.controls['issueDateTo'].setValue(this.issueDateTo);
      this.certificateReportForm.controls['dealerId'].setValue(this.reportSearch.dealerId);
      this.certificateReportForm.controls['policyNumber'].setValue(this.reportSearch.policyNumber);
      this.gridReady = true;
      this.loading = false;
      this.packageService.setPrintCertificateState(null);
    }
    else {
      this.configDataService.getTodaysDate()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (x) => {
            this.startDate = new Date(new Date(x).setHours(0, 0, 0, 0)).toISOString();
            this.currentDate = new Date(x);
            if (this.requestCertificate) {
              this.issueDateFrom = { year: this.currentDate.getFullYear() - 1, month: 1, day: 1 };
              this.reportSearch.statuses = ["Cancelation Requested", "Endorsement Requested"];
            }
            else {
              this.issueDateFrom = { year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate() };
              this.reportSearch.statuses = [];
            }
            this.certificateReportForm.controls['issueDateFrom'].setValue(this.issueDateFrom);
            this.reportSearch.packageNumber = "";
            this.reportSearch.userId = this.user.id;
            this.issueDateTo = { year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate() };
            this.certificateReportForm.controls['issueDateTo'].setValue(this.issueDateTo);

            this.reportSearch.issueDateTo = this.currentDate.getFullYear() + "-" + ('0' + (this.currentDate.getMonth() + 1)).slice(-2) + "-" + ('0' + this.currentDate.getDate()).slice(-2);
            this.reportSearch.policyNumber = "";
            if ((this.user.role != "admin") && (this.user.role != "employee")) {
              this.onSubmit();
            }
            else {
              this.loading = false;
            }
          },
          error: () => {
            this.alertService.error('Failed to load date');
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.detachedMenus.forEach(({ menu }) => menu.remove());
    this.detachedMenus.clear();
  }

  get f() { return this.certificateReportForm.controls; }
  get cancelRequestF() { return this.cancelPackageRequestForm.controls; }

  onSubmit() {
    this.submitted = true;
    this.loading = true;
    this.alertService.clear();

    this.reportSearch = new ReportSearch();
    this.reportSearch.userId = this.user.id;
    if (this.requestCertificate) {
      this.reportSearch.statuses = ["Cancelation Requested", "Endorsement Requested"];
    }
    else {
      this.reportSearch.statuses = [];
    }

    this.reportSearch.packageNumber = this.certificateReportForm.get('packageNumber').value;
    this.reportSearch.chasisNumber = this.certificateReportForm.get('chasisNumber').value;

    this.reportSearch.firstName = this.certificateReportForm.get('firstName').value;
    this.reportSearch.lastName = this.certificateReportForm.get('lastName').value;
    this.reportSearch.userName = this.certificateReportForm.get('userName').value;
    this.reportSearch.policyNumber = this.certificateReportForm.get('policyNumber').value;
    this.issueDateFrom = this.certificateReportForm.get('issueDateFrom').value;
    this.reportSearch.issueDateFrom = this.issueDateFrom.year + '-' + ('0' + this.issueDateFrom.month).slice(-2) + '-' + ('0' + this.issueDateFrom.day).slice(-2);
    this.issueDateFrom = this.certificateReportForm.get('issueDateTo').value;
    this.reportSearch.issueDateTo = this.issueDateFrom.year + '-' + ('0' + this.issueDateFrom.month).slice(-2) + '-' + ('0' + this.issueDateFrom.day).slice(-2);

    this.searchService.getPackageReport(this.reportSearch)
      .pipe(first())
      .subscribe({
        next: (x) => {
          this.packages = x;
          this.gridReady = true;
          this.loading = false;
        },
        error: error => {
          this.alertService.error(error)
          this.loading = false;
        }
      })
  }

  private filterPackages(skip?: 'type' | 'status' | 'enteredBy'): Package[] {
    return (this.packages || []).filter(p =>
      (skip === 'type' || this.selectedTypes.size === 0 || this.selectedTypes.has(p.type == 'Old' ? 'Renewal' : 'New')) &&
      (skip === 'status' || this.selectedStatuses.size === 0 || this.selectedStatuses.has(p.packageStatus)) &&
      (skip === 'enteredBy' || this.selectedEnteredBy.size === 0 || this.selectedEnteredBy.has(p.userNameProcessed)));
  }

  get visiblePackages(): Package[] {
    return this.filterPackages();
  }

  get summary() {
    // each group's counts reflect the other groups' selections
    const typeList = this.filterPackages('type');
    const renewal = typeList.filter(p => p.type == 'Old').length;

    const statusCounts = new Map<string, number>([['Submitted', 0], ['Print Ready', 0]]);
    this.filterPackages('status').forEach(p => statusCounts.set(p.packageStatus, (statusCounts.get(p.packageStatus) || 0) + 1));

    const enteredByCounts = new Map<string, number>();
    this.filterPackages('enteredBy').forEach(p => {
      if (p.userNameProcessed) {
        enteredByCounts.set(p.userNameProcessed, (enteredByCounts.get(p.userNameProcessed) || 0) + 1);
      }
    });
    this.selectedEnteredBy.forEach(n => { if (!enteredByCounts.has(n)) { enteredByCounts.set(n, 0); } });

    return {
      total: this.visiblePackages.length,
      newCount: typeList.length - renewal,
      renewalCount: renewal,
      statuses: Array.from(statusCounts, ([status, count]) => ({ status, count })),
      enteredBy: Array.from(enteredByCounts, ([name, count]) => ({ name, count }))
    };
  }

  toggleFilter(set: Set<string>, value: string) {
    const wasSelected = set.has(value);
    set.clear();
    if (!wasSelected) {
      set.add(value);
    }
  }

  onReset() {
    this.alertService.clear();
    window.location.reload();
  }

  downloadPackage(packageInput: Package) {
    this.packageService.printPackage = packageInput;
    this.fileService.getPdfData(this.packageService.printPackage.packageNumber + "-Package.pdf").pipe(first())
      .subscribe({
        next: data => {
          this.triggerFileDownload(data, this.packageService.printPackage.packageNumber + ".pdf");
        },
        error: error => {
          this.alertService.error(error);
        }
      });
  }

  private triggerFileDownload(data: Blob, fileName: string): void {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(data);
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 60000);
  }

  selectCancelPackage(certificate: Package) {
    this.canceledPackageSelect = certificate;
    this.packageService.printPackage = certificate;
    if (certificate.certificateNumber == null || certificate.certificateNumber == "") {
      this.cancelRequestF.packageAmountReversed.setValue(true);
    }
    else {
      this.cancelRequestF.packageAmountReversed.setValue(false);
    }
    this.packageService.setCurrentPackage(certificate);
  }

  cancelPackage() {
    this.isDeleting = true;
    if (this.cancelPackageRequestForm.invalid) {
      this.isDeleting = false;
      return;
    }
    this.canceledPackageSelect.comment = this.cancelRequestF.cancelReason.value;
    this.packageService.cancelPackage(this.canceledPackageSelect).pipe(first())
      .subscribe({
        next: (y) => {
          this.packages = this.packages.map(x => {
            if (x.packageNumber == this.canceledPackageSelect.packageNumber) {
              x.packageStatus = "Cancelled";
              x.requestHistory = y.requestHistory;
              this.packageService.setCurrentPackage(x);
            }
            this.isDeleting = false;
            return x;
          });
          this.canceledPackageSelect.packageStatus = "Cancelled";
        },
        error: error => {
          this.alertService.error(error);
          this.isDeleting = false;
        }
      })
  }

  endorsePackage() {
    this.isDeleting = true;
    if (this.cancelPackageRequestForm.invalid) {
      this.isDeleting = false;
      return;
    }
    this.canceledPackageSelect.comment = this.cancelRequestF.cancelReason.value;
    this.packageService.endorsePackage(this.canceledPackageSelect).pipe(first())
      .subscribe({
        next: (y) => {
          this.packages = this.packages.map(x => {
            if (x.packageNumber == this.canceledPackageSelect.packageNumber) {
              x.packageStatus = "Cancelled";
              x.requestHistory = y.requestHistory;
              this.packageService.setCurrentPackage(x);
            }
            this.isDeleting = false;
            return x;
          });
          this.canceledPackageSelect.packageStatus = "Cancelled";
        },
        error: error => {
          this.alertService.error(error);
          this.isDeleting = false;
        }
      })
  }

  requestCancelPackage() {
    this.isDeleting = true;
    if (this.cancelPackageRequestForm.invalid) {
      this.isDeleting = false;
      return;
    }
    this.canceledPackageSelect.comment = this.cancelRequestF.cancelReason.value;

    this.packageService.requestCancelPackage(this.canceledPackageSelect).pipe(first())
      .subscribe({
        next: (y) => {
          this.packages = this.packages.map(x => {
            if (x.packageNumber == y.packageNumber) {
              x.packageStatus = "Cancelation Requested";
              x.requestHistory = y.requestHistory;
              this.packageService.setCurrentPackage(x);
              this.canceledPackageSelect.packageStatus = "Cancelation Requested";
            }
            this.isDeleting = false;
            this.closeRequestCancelModalBtn.nativeElement.click();
            return x;
          });
        },
        error: error => {
          this.alertService.error(error);
          this.isDeleting = false;
        }
      })
  }

  requestEndorsePackage() {
    this.isDeleting = true;
    if (this.cancelPackageRequestForm.invalid) {
      this.isDeleting = false;
      return;
    }
    this.canceledPackageSelect.comment = this.cancelRequestF.cancelReason.value;
    this.packageService.setCurrentPackage(this.canceledPackageSelect)

    this.packageService.requestEndorsePackage(this.canceledPackageSelect).pipe(first())
      .subscribe({
        next: (y) => {
          this.packages = this.packages.map(x => {
            if (x.packageNumber == y.packageNumber) {
              x.packageStatus = "Endorsement Requested";
              x.requestHistory = y.requestHistory;
              this.packageService.setCurrentPackage(x);
              this.canceledPackageSelect.packageStatus = "Endorsement Requested";
            }
            this.isDeleting = false;
            return x;
          });
        },
        error: error => {
          this.alertService.error(error);
          this.isDeleting = false;
        }
      })
  }

  processPackage(packageDtl: Package) {
    this.packageService.printPackage = packageDtl;
    this.setNewPrintCertificateState();
    this.router.navigate(['../issueCertificate/processPackage'], { relativeTo: null });
  }

  viewPackage(packageDtl: Package) {
    this.packageService.printPackage = packageDtl;
    this.setNewPrintCertificateState();
    this.router.navigate(['../issueCertificate/viewPackage'], { relativeTo: null });
  }

  preparePackage(packageDtl: Package) {
    this.packageService.printPackage = packageDtl;
    this.setNewPrintCertificateState();
    this.router.navigate(['../print'], { relativeTo: null });
  }

  setNewPrintCertificateState() {
    this.printCertificateState = new PrintCertificateState();
    this.printCertificateState.reportSearch = this.reportSearch;
    this.printCertificateState.packageList = this.packages;
    this.packageService.setPrintCertificateState(this.printCertificateState);
  }

  uploadPackage(packageDtl: Package) {
    this.packageService.printPackage = packageDtl;
  }

  onPolicyPdfSelected(event) {
    this.uploadSuccess = false;
    this.modelLoading = true;
    this.selectedFile = event.target.files[0];
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('customFileName', this.packageService.printPackage.packageNumber + "-Inspolicy");
    this.fileService.fileUpload(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.packageService.printPackage.packageStatus = "Certificate Generated";
          this.packageService.updatePackageStatus().pipe(first()).subscribe({
            next: a => {
              this.modelLoading = false;
            },
            error: error => {
              this.alertService.error(error)
              this.modelLoading = false;
            }
          });
          this.uploadSuccess = true;
        },
        error: () => {
          this.alertService.error('Failed to upload file');
          this.modelLoading = false;
        }
      });
  }

  isAnyActionAvailable(packageInput: any, user: any): boolean {
    return (packageInput.packageStatus == 'Submitted' && ((user.role == 'admin' || user.role == 'employee') || (user.specialRoleAccess && user.specialRoleAccess.includes('CanGenerateOldPolicyPackage') && packageInput.type == 'Old'))) ||
      ((packageInput.packageStatus == 'Print Ready' || (packageInput.packageStatus == 'Certificate Generated' && user.role == 'admin')) && (user.role == 'admin' || user.role == 'employee')) ||
      ((user.role == 'admin' || user.role == 'employee')) ||
      (['Certificate Generated', 'Submitted', 'Print Ready'].includes(packageInput.packageStatus) && (user.role == 'admin')) ||
      (packageInput.packageStatus == 'Print Ready');
  }

  private detachedMenus = new Map<string, { menu: HTMLElement; parent: HTMLElement }>();

  onDropdownShow(event: Event, packageNumber: string): void {
    // Add class to table wrapper for CSS fallback
    const tableWrapper = document.querySelector('.package-list-table-wrap');
    if (tableWrapper) {
      tableWrapper.classList.add('dropdown-overlay-active');
    }

    // Add active class to the current dropdown's table row
    const currentRow = (event.target as HTMLElement).closest('tr');
    if (currentRow) {
      currentRow.classList.add('dropdown-active');
    }

    // Get the clicked button element
    const button = event.target as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    
    // Find the dropdown menu that will be shown
    setTimeout(() => {
      const dropdownMenu = button.nextElementSibling as HTMLElement;
      if (dropdownMenu && dropdownMenu.classList.contains('dropdown-menu')) {
        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 220; // Match CSS width
        
        // Calculate initial position
        let leftX = buttonRect.left;
        const topY = buttonRect.bottom + 5;
        
        // Check if popup would go out of bounds on the right
        if (leftX + dropdownWidth > viewportWidth) {
          // Align to right edge of button instead
          leftX = buttonRect.right - dropdownWidth;
          // If still out of bounds, align to viewport edge
          if (leftX < 0) {
            leftX = viewportWidth - dropdownWidth - 10;
          }
        }
        
        // Re-parent to <body> so the table scroller / app container cannot clip the menu
        if (dropdownMenu.parentElement !== document.body) {
          this.detachedMenus.set(packageNumber, { menu: dropdownMenu, parent: dropdownMenu.parentElement as HTMLElement });
          document.body.appendChild(dropdownMenu);
        }

        dropdownMenu.style.left = leftX + 'px';
        dropdownMenu.style.top = topY + 'px';
        dropdownMenu.style.transform = 'none';
      }
    }, 10);
  }

  onDropdownHide(event: Event, packageNumber: string): void {
    // Remove class from table wrapper
    const tableWrapper = document.querySelector('.package-list-table-wrap');
    if (tableWrapper) {
      tableWrapper.classList.remove('dropdown-overlay-active');
    }

    // Remove active class from all table rows
    const tableRows = document.querySelectorAll('tr');
    tableRows.forEach(row => row.classList.remove('dropdown-active'));

    // Put the menu back under its dropdown before resetting its position
    const detached = this.detachedMenus.get(packageNumber);
    if (detached) {
      detached.parent.appendChild(detached.menu);
      this.detachedMenus.delete(packageNumber);
    }

    // Reset any custom positioning
    const button = event.target as HTMLElement;
    const dropdownMenu = button.nextElementSibling as HTMLElement;
    if (dropdownMenu && dropdownMenu.classList.contains('dropdown-menu')) {
      dropdownMenu.style.left = '';
      dropdownMenu.style.top = '';
      dropdownMenu.style.transform = '';
    }
  }

}
